import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Ricerca } from './anagrafiche.js';
import { ErroreApi, chiama } from './cliente.js';
import { t } from '../i18n/lingua.js';

/**
 * L'archivio documentale.
 *
 * Ottantaduemila documenti. Il file non passa mai da qui dentro: si scarica
 * dal servizio, che sa dove sta e con che nome deve uscire — e il nome è la
 * ragione per cui questa sezione è stata rifatta, perché nel v2 metà
 * dell'archivio si chiama `untitled.pdf`.
 */

export interface RigaDocumento {
  id: number;
  title: string;
  protocolNumber: string | null;
  identityId: number | null;
  customer: string | null;
  orderId: number | null;
  orderNumber: string | null;
  orderProtocol: number | null;
  fileName: string | null;
  /** Il nome con cui il file esce davvero. */
  downloadName: string;
  /** Registrato, ma nell'archivio il file non c'è. */
  fileMissing?: boolean;
  extension: string | null;
  createdAt: string;
  categories: string[];
  archived: boolean;
}

export interface Documento extends RigaDocumento {
  description: string | null;
  keywords: string | null;
  notes: string | null;
  customerBcCode: string | null;
  orderSectional: string | null;
  fileFormatId: number | null;
  fileFormat: string | null;
  directory: string | null;
  /** `null` quando il file non si trova nell'archivio. */
  fileSize: number | null;
  /** Quanti altri documenti puntano allo stesso file. */
  sharedWith: number;
  updatedAt: string;
}

/** La scheda ha le categorie per esteso, l'elenco solo i nomi. */
export interface DocumentoCompleto extends Omit<Documento, 'categories'> {
  categories: { id: number; name: string; level: number }[];
}

interface Pagina {
  rows: RigaDocumento[];
  total: number;
  page: number;
  size: number;
  pages: number;
  /** Solo quando nella casella c'è un elenco di PO: quanti erano e quali non hanno documenti. */
  poList?: { searched: number; notFound: string[] };
}

export interface OpzioniDocumenti {
  categories: { id: number; label: string }[];
  categories2: { id: number; label: string }[];
  extensions: string[];
  maxUploadBytes: number;
}

export const RICERCA_DOCUMENTI: Ricerca = {
  page: 1,
  size: 50,
  sort: 'createdAt',
  dir: 'desc',
  q: '',
  archived: false,
  filters: {},
};

function queryString(r: Ricerca, extra: Record<string, string> = {}) {
  return new URLSearchParams({
    page: String(r.page),
    size: String(r.size),
    sort: r.sort,
    dir: r.dir,
    ...(r.q ? { q: r.q } : {}),
    ...(r.archived ? { archived: 'true' } : {}),
    ...Object.fromEntries(
      Object.entries(r.filters ?? {})
        .filter(([, v]) => v !== undefined && v !== '')
        .map(([k, v]) => [k, String(v)]),
    ),
    ...extra,
  }).toString();
}

export function useDocumenti(ricerca: Ricerca) {
  return useQuery({
    queryKey: ['documenti', ricerca],
    queryFn: () => chiama<Pagina>(`/api/documents?${queryString(ricerca)}`),
    placeholderData: (precedente) => precedente,
  });
}

/** Le pastiglie e «Tutti», con la ricerca e i filtri dell'elenco tranne la categoria. */
export function useConteggiCategorie(ricerca: Ricerca) {
  const { categoryId: _fuori, ...filtri } = ricerca.filters ?? {};
  const senzaCategoria = { ...ricerca, filters: filtri, page: 1 };
  return useQuery({
    queryKey: ['documenti-conteggi', senzaCategoria],
    queryFn: () =>
      chiama<{ counts: { id: number; name: string; count: number }[]; total: number }>(
        `/api/documents/counts?${queryString(senzaCategoria)}`,
      ),
    placeholderData: (precedente) => precedente,
  });
}

export function useDocumento(id: number) {
  return useQuery({
    queryKey: ['documento', id],
    queryFn: () => chiama<DocumentoCompleto>(`/api/documents/${id}`),
    enabled: Number.isInteger(id) && id > 0,
  });
}

export function useDefinizioneFiltriDocumenti() {
  return useQuery({
    queryKey: ['filtri-documenti'],
    queryFn: () =>
      chiama<{
        filters: {
          key: string;
          label: string;
          type: 'text' | 'list' | 'yesNo' | 'choice' | 'date';
          options?: string;
          values?: { value: string; label: string }[];
        }[];
        options: Record<string, { id: string | number; label: string }[]>;
      }>('/api/documents/filters'),
    staleTime: 10 * 60_000,
  });
}

export function useOpzioniDocumenti() {
  return useQuery({
    queryKey: ['opzioni-documenti'],
    queryFn: () => chiama<OpzioniDocumenti>('/api/documents/options'),
    staleTime: 10 * 60_000,
  });
}

export function useDocumentiOrdine(ordineId: number) {
  return useQuery({
    queryKey: ['documenti-ordine', ordineId],
    queryFn: () =>
      chiama<{ documents: RigaDocumento[]; total: number }>(`/api/orders/${ordineId}/documents`),
    enabled: Number.isInteger(ordineId) && ordineId > 0,
  });
}

export function useCreaDocumento() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (dati: Record<string, unknown>) =>
      chiama<{ id: number }>('/api/documents', { metodo: 'POST', corpo: dati }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['documenti'] });
      void client.invalidateQueries({ queryKey: ['documenti-conteggi'] });
    },
  });
}

export function useSalvaDocumento(id: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (dati: Record<string, unknown>) =>
      chiama<DocumentoCompleto>(`/api/documents/${id}`, { metodo: 'PATCH', corpo: dati }),
    onSuccess: (documento) => {
      client.setQueryData(['documento', id], documento);
      void client.invalidateQueries({ queryKey: ['documenti'] });
      void client.invalidateQueries({ queryKey: ['documenti-conteggi'] });
    },
  });
}

/**
 * Il file va per conto suo: `fetch` con un `FormData`, non JSON.
 *
 * Non passa da `chiama` perché quello mette `content-type: application/json`,
 * e con un multipart il confine lo deve scrivere il browser.
 */
export function useCaricaFile(id: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const dati = new FormData();
      dati.append('file', file);
      const risposta = await fetch(`/api/documents/${id}/file`, {
        method: 'POST',
        body: dati,
        credentials: 'same-origin',
      });
      const corpo = await risposta.json().catch(() => null);
      if (!risposta.ok) {
        throw new ErroreApi(
          corpo?.error ?? 'erroreCaricamento',
          corpo?.message ?? t('Il caricamento non è riuscito.'),
          risposta.status,
        );
      }
      return corpo as DocumentoCompleto;
    },
    onSuccess: (documento) => {
      client.setQueryData(['documento', id], documento);
      void client.invalidateQueries({ queryKey: ['documenti'] });
    },
  });
}

export interface EsitoMultiplo {
  created: { id: number; orderNumber: string }[];
  unknownOrders: string[];
  fileName: string | null;
}

export function useCreaDocumentiMultipli() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ file, ...dati }: Record<string, unknown> & { file: File | null }) => {
      const corpoDati = new FormData();
      corpoDati.append('data', JSON.stringify(dati));
      if (file) corpoDati.append('file', file);
      const risposta = await fetch('/api/documents/multiple', {
        method: 'POST',
        body: corpoDati,
        credentials: 'same-origin',
      });
      const corpo = await risposta.json().catch(() => null);
      if (!risposta.ok) {
        throw new ErroreApi(
          corpo?.error ?? 'erroreCreazione',
          corpo?.message ?? t('La creazione non è riuscita.'),
          risposta.status,
        );
      }
      return corpo as EsitoMultiplo;
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['documenti'] });
      void client.invalidateQueries({ queryKey: ['documenti-conteggi'] });
      void client.invalidateQueries({ queryKey: ['documenti-ordine'] });
    },
  });
}

/** L'indirizzo da cui scaricare: il browser ci va da solo, col cookie. */
export function indirizzoScarico(id: number): string {
  return `/api/documents/${id}/download`;
}

export function esportaDocumenti(ricerca: Ricerca, formato: 'csv' | 'xlsx') {
  window.location.href = `/api/documents?${queryString(ricerca, { format: formato })}`;
}

/** Quanto pesa, detto come lo direbbe una persona. */
export function peso(byte: number | null): string {
  if (byte === null) return '—';
  if (byte < 1024) return `${byte} byte`;
  if (byte < 1024 * 1024) return `${(byte / 1024).toLocaleString('it-IT', { maximumFractionDigits: 0 })} KB`;
  return `${(byte / 1024 / 1024).toLocaleString('it-IT', { maximumFractionDigits: 1 })} MB`;
}
